import { type Collection, isCollection, isSameCollectionType } from './collection';

/**
 * Compare 2 values to see if they are equal (shallower than deepEquals)
 */
export function equals(valueA: any, valueB: any): boolean {
    // biome-ignore lint/suspicious/noSelfCompare: We check for NaN.
    if (valueA === valueB || (valueA !== valueA && valueB !== valueB)) {
        return true;
    }
    if (!valueA || !valueB) {
        return false;
    }

    if (typeof valueA.valueOf === 'function' && typeof valueB.valueOf === 'function') {
        valueA = valueA.valueOf();
        valueB = valueB.valueOf();
        // biome-ignore lint/suspicious/noSelfCompare: We check for NaN.
        if (valueA === valueB || (valueA !== valueA && valueB !== valueB)) {
            return true;
        }
        if (!valueA || !valueB) {
            return false;
        }
    }

    if (typeof valueA.equals === 'function') {
        return valueA.equals(valueB);
    }

    return false;
}

type DeepEqualsOptions = {
    /**
     * If true, undefined values will be considered the same as non-existing.
     * @default false
     */
    ignoreKeyedUndefined?: boolean;
};

/**
 * Deeply compare 2 values to see if they are equal.
 * It can work with any JS objects, including immutable ones.
 */
export function deepEquals(valueA: any, valueB: any, options: DeepEqualsOptions = {}): boolean {
    if (equals(valueA, valueB)) {
        return true;
    }

    if (!valueA || !valueB) {
        return false;
    }

    if (isCollection(valueA)) {
        if (!isSameCollectionType(valueA, valueB)) {
            return false;
        }

        const sizeA = valueA.size;
        const sizeB = valueB.size;

        // @ts-ignore
        if (valueA.constructor.isOrdered) {
            if (sizeA !== sizeB) {
                return false;
            }
            return areOrderedCollectionsEqual(valueA, valueB, options);
        }

        // @ts-ignore
        if (valueA.constructor.isKeyed) {
            if (!options.ignoreKeyedUndefined && sizeA !== sizeB) {
                return false;
            }
            return areKeyedCollectionsEqual(valueA, valueB, options);
        }

        throw new Error('Unknown collection type');
    }

    if (Array.isArray(valueA) || Array.isArray(valueB)) {
        if (!Array.isArray(valueA) || !Array.isArray(valueB)) {
            return false;
        }
        if (valueA.length !== valueB.length) {
            return false;
        }
        for (let ii = 0; ii < valueA.length; ii++) {
            if (!deepEquals(valueA[ii], valueB[ii], options)) {
                return false;
            }
        }
        return true;
    }

    if (typeof valueA === 'object' || typeof valueB === 'object') {
        if (typeof valueA !== 'object' || typeof valueB !== 'object') {
            return false;
        }
        const keysA = getComparableObjectKeys(valueA, options);
        const keysB = getComparableObjectKeys(valueB, options);
        if (keysA.length !== keysB.length) {
            return false;
        }

        for (let ii = 0; ii < keysA.length; ii++) {
            const key = keysA[ii];
            if (key === undefined || !deepEquals(valueA[key], valueB[key], options)) {
                return false;
            }
        }
        return true;
    }

    return false;
}

function getComparableObjectKeys(
    object: Record<string, any>,
    options: DeepEqualsOptions
): string[] {
    if (!options.ignoreKeyedUndefined) {
        return Object.keys(object);
    }

    return Object.keys(object).filter((key) => object[key] !== undefined);
}

function getCollectionKeys(
    collection: Collection<any, any>,
    options: DeepEqualsOptions
): Array<string | number | symbol> {
    const keys = collection.toKeysArray();
    if (!options.ignoreKeyedUndefined) {
        return keys;
    }

    return keys.filter((key) => {
        const entry = collection.getByKey(key);
        return entry !== undefined && entry[1] !== undefined;
    });
}

function areKeyedCollectionsEqual(
    colA: Collection<any, any>,
    colB: Collection<any, any>,
    options: DeepEqualsOptions
): boolean {
    const keysA = getCollectionKeys(colA, options);
    const keysB = getCollectionKeys(colB, options);

    if (keysA.length !== keysB.length) {
        return false;
    }

    for (let index = 0; index < keysA.length; index++) {
        const key = keysA[index];
        const entryA = colA.getByKey(key);
        const entryB = colB.getByKey(key);

        if (!entryA || !entryB) {
            return false;
        }

        if (!deepEquals(entryA[1], entryB[1], options)) {
            return false;
        }
    }

    return true;
}

function areOrderedCollectionsEqual(
    colA: Collection<any, any>,
    colB: Collection<any, any>,
    options: DeepEqualsOptions
): boolean {
    const entriesA = colA.toEntriesArray();
    const entriesB = colB.toEntriesArray();

    if (entriesA.length !== entriesB.length) {
        return false;
    }

    for (let index = 0; index < entriesA.length; index++) {
        const entryA = entriesA[index];
        const entryB = entriesB[index];

        if (!entryA || !entryB) {
            return false;
        }

        if (entryA[0] !== entryB[0]) {
            return false;
        }

        if (!deepEquals(entryA[1], entryB[1], options)) {
            return false;
        }
    }

    return true;
}
